import React from 'react';
import {
  Box,
  MenuItem,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import {EGrowthObservationStage} from './AddNewDataPointForm.constants';
import {IGrowthObservationFormProps} from './AddNewDataPointForm.types';

export const GrowthObservationForm: React.FC<IGrowthObservationFormProps> = ({
  stage,
  onStageChange,
  data,
  onDataChange,
}) => {
  const handleChange = (field: keyof typeof data, value: any) => {
    onDataChange({...data, [field]: value});
  };

  const renderField = (
    label: string,
    field: keyof typeof data,
    type: string = 'text',
  ) => (
    <Box mb={2}>
      <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 6}}>
        {label}
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        type={type}
        value={data[field] || ''}
        onChange={(e) => handleChange(field, e.target.value)}
        InputProps={{style: {backgroundColor: '#fff', borderRadius: 8}}}
      />
    </Box>
  );

  const renderCheckbox = (label: string, field: keyof typeof data) => (
    <Box mb={2}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!data[field]}
            onChange={(e) => handleChange(field, e.target.checked)}
            color="primary"
          />
        }
        label={<Typography style={{fontSize: 14}}>{label}</Typography>}
      />
    </Box>
  );

  return (
    <Box mt={2}>
      <Box mb={3}>
        <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 8}}>
          Growth Observation Stage
        </Typography>
        <TextField
          select
          fullWidth
          variant="outlined"
          size="small"
          value={stage}
          onChange={(e) => onStageChange(e.target.value)}
          InputProps={{style: {backgroundColor: '#fff', borderRadius: 8}}}
        >
          {Object.values(EGrowthObservationStage).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {stage && (
        <>
          {renderField('Observation Date', 'observationDate', 'date')}

          {stage === EGrowthObservationStage.PRE_PLANT && (
            <>
              {renderField(
                'Latest Acceptable Planting date',
                'latestAcceptablePlantingDate',
                'date',
              )}
              {renderField(
                'Expected Germination Rate (%)',
                'expectedGerminationRate',
              )}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
            </>
          )}

          {stage === EGrowthObservationStage.PLANT && (
            <>
              {renderField(
                'Latest Acceptable Planting date',
                'latestAcceptablePlantingDate',
                'date',
              )}
              {renderField('Planned Ha', 'plannedHa')}
              {renderField('Ha Prepared', 'haPrepared')}
              {renderField(
                'Expected Germination Rate (%)',
                'expectedGerminationRate',
              )}
              {renderField('Row Spacing (cm)', 'rowSpacing')}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
              {renderField('% Planted', 'plantedPct')}
            </>
          )}

          {(stage === EGrowthObservationStage.POST_PLANTING ||
            stage === EGrowthObservationStage.GERMINATION) && (
            <>
              {renderField('Plant Height (cm)', 'plantHeightCm')}
              {renderField('Germination Rate (%)', 'germinationRatePct')}
              {renderField('Root Length Scale (cm)', 'rootLengthScale')}
              {renderField(
                'Crop Maturity Assessment',
                'cropMaturityAssessment',
              )}
              {renderField('Uniformity of Maturity', 'uniformityOfMaturity')}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
              {renderField('% Planted', 'plantedPct')}
            </>
          )}

          {stage === EGrowthObservationStage.VEGETATIVE_GROWTH && (
            <>
              {renderField('Plant Height (cm)', 'plantHeightCm')}
              {renderField('Leaves Per Plant', 'leavesPerPlant')}
              {renderField(
                'Flowering Uniformity Scale',
                'floweringUniformityScale',
              )}
              {renderField(
                'Crop Maturity Assessment',
                'cropMaturityAssessment',
              )}
              {renderField('Uniformity of Maturity', 'uniformityOfMaturity')}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
              {renderField('% Planted', 'plantedPct')}
              {renderCheckbox('Market Ready', 'marketReady')}
              {data.marketReady &&
                renderField('Market Ready Comment', 'marketReadyComment')}
              {renderField('Tons Hedged', 'tonsHedged')}
            </>
          )}

          {stage === EGrowthObservationStage.FRUIT_SET_BULKING && (
            <>
              {renderField(
                'Flowering Uniformity Scale',
                'floweringUniformityScale',
              )}
              {renderField('Fruit Set Percent (%)', 'fruitSetPercent')}
              {renderField(
                'Fruit Set Uniformity Scale',
                'fruitSetUniformityScale',
              )}
              {renderField('Fruit Avg Length (cm)', 'fruitAvgLengthCm')}
              {renderField('Fruit Avg Diameter (cm)', 'fruitAvgDiameterCm')}
              {renderField(
                'Crop Maturity Assessment',
                'cropMaturityAssessment',
              )}
              {renderField(
                '% Crop Ready for Harvest',
                'pctCropReadyForHarvest',
              )}
              {renderField('Uniformity of Maturity', 'uniformityOfMaturity')}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
              {renderCheckbox('Market Ready', 'marketReady')}
              {data.marketReady &&
                renderField('Market Ready Comment', 'marketReadyComment')}
              {renderField('Tons Hedged', 'tonsHedged')}
            </>
          )}

          {stage === EGrowthObservationStage.PRE_HARVEST && (
            <>
              {renderField('Fruit Set Percent (%)', 'fruitSetPercent')}
              {renderField(
                'Fruit Set Uniformity Scale',
                'fruitSetUniformityScale',
              )}
              {renderField('Fruit Avg Length (cm)', 'fruitAvgLengthCm')}
              {renderField('Fruit Avg Diameter (cm)', 'fruitAvgDiameterCm')}
              {renderField(
                'Crop Maturity Assessment',
                'cropMaturityAssessment',
              )}
              {renderField(
                '% Crop Ready for Harvest',
                'pctCropReadyForHarvest',
              )}
              {renderField('Uniformity of Maturity', 'uniformityOfMaturity')}
              {renderField('Estimated Yield (Tons)', 'estimatedYield')}
              {renderField(
                'Estimated Harvest Date',
                'estimatedHarvestDate',
                'date',
              )}
              {renderCheckbox('Logistics Ready', 'logisticsReady')}
              {renderCheckbox('Market Ready', 'marketReady')}
              {data.marketReady &&
                renderField('Market Ready Comment', 'marketReadyComment')}
              {renderField('Tons Hedged', 'tonsHedged')}
            </>
          )}
        </>
      )}
    </Box>
  );
};
